import React, { useEffect, useState } from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { useSettings } from '../App';
import { useT } from '../lib/i18n';
import { blogExternalAPI } from '../lib/api';

// ─────────────────────────────────────────────────────────────────────────
// ModernInsights — the «Moderno» theme Blog / Insights block (s139, approved by
// Anthony: I2 "Category Rows"). One row per KMS category picked in CMS →
// Section Manager → Blog — Section Configuration (kms_categories, in that
// order): the category name with "View all" to that category in the KMS, then
// its latest 3 reports as image cards. No categories picked → one row with the
// latest reports. Header (eyebrow/title/subtitle/CTA) from the same config.
// Fix vs the old block: it now passes the configured categories to the KMS
// feed, so a newly added category shows up. Colours from theme tokens.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'Playfair Display', Georgia, serif";
const PER = 3;
const fmt = (d) => { const x = new Date(String(d || '').replace(' ', 'T')); return isNaN(x) ? '' : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };

function Card({ p }) {
  const [broken, setBroken] = useState(false);
  return (
    <a href={p.url || p.link} target="_blank" rel="noopener noreferrer" className="group bg-white flex flex-row md:flex-col overflow-hidden" style={{ border: '1px solid rgba(20,26,58,.08)' }} data-testid="insights-card">
      <div className="w-28 flex-none self-stretch md:w-auto md:self-auto md:aspect-[16/9] overflow-hidden" style={{ background: 'linear-gradient(150deg, color-mix(in srgb, var(--color-accent, #ff9c1b) 30%, #fff), var(--color-accent, #ff9c1b))' }}>
        {p.image && !broken && <img src={p.image} alt="" onError={() => setBroken(true)} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />}
      </div>
      <div className="flex flex-col gap-1.5 md:gap-2 p-4 md:p-5 flex-1 min-w-0">
        {fmt(p.date) && <span className="text-[11px] font-bold uppercase" style={{ letterSpacing: '.12em', color: 'var(--accent-strong, #b86500)' }}>{fmt(p.date)}</span>}
        <h4 className="font-bold transition-colors duration-200 group-hover:text-[color:var(--accent-strong)]" style={{ fontFamily: SERIF, fontSize: 'clamp(1rem, 1.2vw, 1.15rem)', lineHeight: 1.3, color: 'var(--color-heading, #1a2332)', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.title}</h4>
        <span className="hidden md:inline-flex mt-auto pt-2 items-center gap-1.5 text-xs font-bold uppercase" style={{ letterSpacing: '.06em', color: 'var(--accent-strong, #b86500)' }}>Read report <ArrowUpRight className="w-3.5 h-3.5" /></span>
      </div>
    </a>
  );
}

export default function ModernInsights({ cmsConfig = {} }) {
  const tt = useT();
  const settings = useSettings();
  const cats = (Array.isArray(cmsConfig.kms_categories) ? cmsConfig.kms_categories : []).filter(c => c && c.slug);
  const slugs = cats.map(c => c.slug).join(',');
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!settings.blog_api_url) { setRows([]); return undefined; }
    let cancelled = false;
    const req = slugs
      ? blogExternalAPI.getLatest(slugs, PER).then(r => (r.data?.sections || []).map(s => ({ slug: s.slug, label: s.label || s.name, url: s.url, posts: s.posts || [] })))
      : blogExternalAPI.getLatest().then(r => [{ slug: '', label: '', url: '', posts: r.data?.posts || [] }]);
    req.then(r => { if (!cancelled) setRows(r); }).catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [settings.blog_api_url, slugs]);

  const visible = (rows || []).map(r => ({ ...r, posts: r.posts.filter(p => p && p.title).slice(0, PER) })).filter(r => r.posts.length);
  if (rows && !visible.length) return null;

  const labelFor = (r) => { const c = cats.find(x => x.slug === r.slug); return (c && c.label && tt(c.label)) || r.label; };
  const title = tt(cmsConfig.title) || 'Insights';
  const words = title.trim().split(/\s+/);
  const last = words.pop();
  const ctaText = tt(cmsConfig.cta_text);
  const ctaUrl = tt(cmsConfig.cta_url);

  return (
    <section id="blog" data-testid="blog-section" style={{ background: 'color-mix(in srgb, var(--color-accent, #ff9c1b) 5%, #f6f5f2)' }}>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-[clamp(24px,5vw,80px)] py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div className="flex flex-col gap-4 max-w-2xl">
            {tt(cmsConfig.eyebrow) && (
              <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase" style={{ letterSpacing: '.2em', color: 'var(--accent-strong, #b86500)' }}>
                <span style={{ width: 32, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{tt(cmsConfig.eyebrow)}
              </div>
            )}
            <h2 className="font-bold" style={{ fontFamily: SERIF, fontSize: 'clamp(2rem, 3.4vw, 3rem)', lineHeight: 1.08, color: 'var(--color-heading, #1a2332)' }} data-testid="blog-title">
              {words.join(' ')}{words.length ? ' ' : ''}<em style={{ fontStyle: 'italic', fontWeight: 500, color: 'var(--accent-strong, #b86500)' }}>{last}</em>
            </h2>
            {tt(cmsConfig.subtitle) && <p className="text-base md:text-lg leading-relaxed" style={{ color: 'var(--color-body-text, #475569)' }}>{tt(cmsConfig.subtitle)}</p>}
          </div>
          {ctaText && ctaUrl && (
            <a href={ctaUrl} target={cmsConfig.cta_new_tab ? '_blank' : '_self'} rel="noopener noreferrer" className="mx-link inline-flex items-center gap-2 font-bold text-sm self-start md:self-end" style={{ color: 'var(--accent-strong, #b86500)' }} data-testid="blog-cta">
              {ctaText} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* while loading, reserve one row per configured category (no layout jump) */}
        <div className="flex flex-col gap-14">
          {(rows ? visible : Array.from({ length: Math.max(1, cats.length) }, (_, i) => ({ slug: `r${i}`, posts: [] }))).map(r => (
            <div key={r.slug || 'latest'} data-testid={`insights-row-${r.slug || 'latest'}`}>
              {(labelFor(r) || r.url) && (
                <div className="flex items-center justify-between gap-4 mb-5">
                  <span className="inline-flex items-center gap-3 text-xs font-bold uppercase" style={{ letterSpacing: '.16em', color: 'var(--color-heading, #1a2332)' }}>
                    <span style={{ width: 22, height: 2, background: 'var(--color-accent, #ff9c1b)' }} />{labelFor(r)}
                  </span>
                  {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="mx-link inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: 'var(--accent-strong, #b86500)' }}>View all <ArrowRight className="w-4 h-4" /></a>}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5" style={rows ? undefined : { minHeight: 360, visibility: 'hidden' }}>
                {r.posts.map((p, i) => <Card key={p.url || i} p={p} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
